import React, { useContext, useEffect, useMemo, useState, Suspense } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  makeStyles,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Avatar,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
} from '@material-ui/core';
import InputBase from '@material-ui/core/InputBase';

// Ícones
import DashboardIcon from '@material-ui/icons/Dashboard';
import ContactsIcon from '@material-ui/icons/Contacts';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import BuildIcon from '@material-ui/icons/Build';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CachedIcon from '@material-ui/icons/Cached';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PersonIcon from '@material-ui/icons/Person';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import MenuIcon from '@material-ui/icons/Menu';
// Ícones oficiais @material-ui/icons (abrir/fechar menu lateral)
import FormatIndentDecreaseIcon from '@material-ui/icons/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@material-ui/icons/FormatIndentIncrease';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ChatIcon from '@material-ui/icons/Chat';
import ForumIcon from '@material-ui/icons/Forum';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import SearchIcon from '@material-ui/icons/Search';
import MessageIcon from '@material-ui/icons/Message';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ScheduleIcon from '@material-ui/icons/Schedule';
import QueueIcon from '@material-ui/icons/Queue';
import ListIcon from '@material-ui/icons/List';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import SettingsIcon from '@material-ui/icons/Settings';
import ReceiptIcon from '@material-ui/icons/Receipt';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import BusinessIcon from '@material-ui/icons/Business';
import PaletteIcon from '@material-ui/icons/Palette';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import LoyaltyIcon from '@material-ui/icons/Loyalty';
import ExtensionIcon from '@material-ui/icons/Extension';
import AppsIcon from '@material-ui/icons/Apps';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
// import HistoryIcon from '@material-ui/icons/History';
import CampaignIcon from '@mui/icons-material/Campaign';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SendIcon from '@material-ui/icons/Send';
import { FaWhatsapp } from 'react-icons/fa';

import useSettings from '../hooks/useSettings';
import { AuthContext } from '../context/Auth/AuthContext';
import { usePlanPermissions } from '../context/PlanPermissionsContext.jsx';
import NotificationSettingsPopover from '../components/NotificationSettingsPopover/index.jsx';
import UserModal from '../components/UserModal';
import NotificationModal from '../components/NotificationModal';
import BlockedModal from '../components/BlockedModal';
import TrialEndingModal from '../components/TrialEndingModal';
import TrialBanner from '../components/TrialBanner';
import {
  shouldShowTrialEndingModal,
  wasTrialEndingModalShownToday,
  markTrialEndingModalShownToday,
} from '../utils/trialEndingUtils';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import PwaHeadUpdater from '../components/PwaHeadUpdater';
import PwaReinstallModal from '../components/PwaReinstallModal';
import SearchTicketModal from '../components/SearchTicketModal/index.jsx';
import InternalChatWidget from '../components/InternalChatWidget/index.jsx';
import { getBackendUrl } from '../config';
import { unsubscribeAndUnregister } from '../services/pushSubscriptionService';
import f002Image from '../assets/f002.png';
import logo from '../assets/logo.png';

const backendUrl = getBackendUrl();

const collapsedDrawerWidth = 72;
const expandedDrawerWidth = 280;
const TRIAL_BANNER_HEIGHT = 36;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    height: '100%',
    backgroundColor: '#f8f9fa',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      maxHeight: '100%',
      overflow: 'hidden',
    },
  },
  // Banner de trial fixo no topo do viewport, acima do header
  trialBannerWrapper: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 12,
  },
  // Global header: fixed; z-index so it stays above content (WhatsApp/Telegram-style)
  appBar: (props) => ({
    flexShrink: 0,
    zIndex: 10,
    backgroundColor: 'transparent !important',
    background: 'transparent !important',
    color: '#3b82f6',
    boxShadow: 'none !important',
    borderBottom: 'none',
    marginLeft: props.drawerWidth,
    width: `calc(100% - ${props.drawerWidth}px)`,
    transition: 'margin-left 0.2s ease, width 0.2s ease',
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      width: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: 'env(safe-area-inset-top, 0px)',
    },
  }),
  toolbar: {
    minHeight: '64px',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      minHeight: '44px',
      padding: '0 12px',
    },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  menuButton: {
    display: 'none',
    color: '#3b82f6',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
    },
    /* No mobile (sm): esconder menu principal; navegação só pelo footer */
    [theme.breakpoints.down('sm')]: {
      display: 'none !important',
    },
  },
  backButtonConversation: {
    display: 'none',
    color: '#3b82f6',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
    },
  },
  searchContainer: {
    position: 'relative',
    backgroundColor: '#f3f4f6',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    width: '100%',
    maxWidth: '400px',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#3b82f6',
    },
    '&:focus-within': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mobileLogo: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      '& img': {
        height: '28px',
        width: 'auto',
      },
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#000000',
  },
  searchButton: (props) => ({
    position: 'absolute',
    right: '4px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: props.primaryColor || '#3b82f6',
    color: '#ffffff',
    borderRadius: '8px',
    padding: '8px',
    minWidth: '36px',
    height: '36px',
    '&:hover': {
      backgroundColor: props.primaryColor || '#2563eb',
      filter: 'brightness(0.9)',
    },
  }),
  inputRoot: {
    color: '#3b82f6',
    width: '100%',
  },
  inputInput: {
    padding: theme.spacing(1.5, 6, 1.5, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
    transition: theme.transitions.create('width'),
    width: '100%',
    fontSize: '14px',
    color: '#3b82f6',
    '&::placeholder': {
      color: '#000000',
      opacity: 1,
    },
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      gap: '4px',
    },
  },
  iconButton: (props) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    color: '#ffffff',
    backgroundColor: props.primaryColor || '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    '& .MuiSvgIcon-root': {
      fontSize: '20px',
      color: '#ffffff',
    },
    '&:hover': {
      backgroundColor: props.primaryColor || '#2563eb',
      filter: 'brightness(0.9)',
      transform: 'scale(1.05)',
    },
    [theme.breakpoints.down('sm')]: {
      width: '36px',
      height: '36px',
      '& .MuiSvgIcon-root': { fontSize: '18px' },
    },
  }),
  avatar: (props) => ({
    width: '45px',
    height: '45px',
    cursor: 'pointer',
    border: '2px solid #e5e7eb',
    backgroundColor: props.primaryColor || '#3b82f6',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: props.primaryColor || theme.palette.primary.dark,
      filter: 'brightness(0.9)',
      transform: 'scale(1.05)',
    },
    [theme.breakpoints.down('sm')]: {
      width: '36px',
      height: '36px',
    },
  }),
  dropdownAvatar: (props) => ({
    width: '32px',
    height: '32px',
    backgroundColor: props.primaryColor || '#3b82f6',
    fontSize: '1.1rem',
  }),
  // Sidebar Styles - Escuro com ícones
  drawer: (props) => ({
    width: props.drawerWidth,
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  }),
  drawerPaper: (props) => ({
    width: props.drawerWidth,
    backgroundColor: 'var(--wl-menu-bg, #3b82f6)',
    borderRight: 'none',
    borderTopRightRadius: 18,
    borderBottomRightRadius: 18,
    overflowX: 'hidden',
    overflowY: 'auto',
    scrollbarWidth: 'none',
    transition: 'width 0.2s ease, background-color 0.3s ease',
    [theme.breakpoints.down('md')]: {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  }),
  mobileDrawer: {
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  drawerHeader: (props) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: props.drawerWidth > collapsedDrawerWidth ? 'space-between' : 'center',
    gap: '8px',
    padding: props.drawerWidth > collapsedDrawerWidth ? '16px 12px' : '16px 0',
    minHeight: '64px',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: 'var(--wl-menu-bg, #3b82f6)',
  }),
  drawerHeaderLogo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  menuToggleButton: {
    color: 'var(--wl-menu-text, #fff)',
    padding: '6px',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  menuToggleIconCombo: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      color: 'var(--wl-menu-icon, #fff)',
    },
  },
  logo: (props) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: props.drawerWidth > collapsedDrawerWidth ? 'flex-start' : 'center',
    cursor: 'pointer',
    width: '100%',
  }),
  logoIcon: (props) => ({
    fontSize: props.drawerWidth > collapsedDrawerWidth ? '36px' : '32px',
    color: '#3b82f6',
    transition: 'font-size 0.2s ease',
  }),
  logoText: {
    display: 'none',
  },
  companyLogo: (props) => ({
    height: '36px',
    width: props.drawerWidth > collapsedDrawerWidth ? '160px' : '36px',
    objectFit: 'contain',
    borderRadius: '8px',
    transition: 'width 0.2s ease',
  }),
  sidebarContent: (props) => ({
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: props.drawerWidth > collapsedDrawerWidth ? '12px 8px' : '8px 0',
    width: '100%',
    boxSizing: 'border-box',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  }),
  menuSectionLabel: (props) => ({
    display: props.drawerWidth > collapsedDrawerWidth ? 'block' : 'none',
    fontSize: '0.68rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--wl-menu-text, rgba(255, 255, 255, 0.75))',
    margin: '12px 16px 4px',
  }),
  menuList: (props) => ({
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: props.drawerWidth > collapsedDrawerWidth ? 'stretch' : 'center',
    gap: '4px',
  }),
  menuItem: (props) => ({
    padding: props.drawerWidth > collapsedDrawerWidth ? '10px 14px' : '12px',
    margin: '0',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    minHeight: 'auto',
    width: props.drawerWidth > collapsedDrawerWidth ? '100%' : '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: props.drawerWidth > collapsedDrawerWidth ? 'flex-start' : 'center',
    gap: props.drawerWidth > collapsedDrawerWidth ? '12px' : 0,
    '&:hover:not(.active)': {
      backgroundColor: 'var(--wl-menu-hover-bg, rgba(0, 0, 0, 0.12))',
      '& .MuiListItemIcon-root': {
        color: 'var(--wl-menu-hover-text, #ffffff)',
      },
      '& .MuiListItemText-primary': {
        color: 'var(--wl-menu-hover-text, #ffffff)',
      },
    },
    '&.active': {
      backgroundColor: 'var(--wl-menu-active-bg, rgba(0, 0, 0, 0.18))',
      borderLeft: '3px solid var(--wl-menu-active-indicator, #7DFFAF)',
      '& .MuiListItemIcon-root': {
        color: 'var(--wl-menu-active-text, #ffffff)',
      },
      '& .MuiListItemText-primary': {
        color: 'var(--wl-menu-active-text, #ffffff)',
      },
    },
  }),
  menuIcon: (props) => ({
    minWidth: props.drawerWidth > collapsedDrawerWidth ? 'auto' : 'initial',
    color: 'var(--wl-menu-icon, #ffffff)',
    margin: props.drawerWidth > collapsedDrawerWidth ? '0 0 0 4px' : 0,
    '& .MuiSvgIcon-root': {
      fontSize: '22px',
    },
  }),
  menuText: (props) => ({
    display: props.drawerWidth > collapsedDrawerWidth ? 'block' : 'none',
    color: 'var(--wl-menu-text, #f1f5f9)',
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: '0.2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  }),
  menuItemExpandIcon: (props) => ({
    marginLeft: 'auto',
    display: props.drawerWidth > collapsedDrawerWidth ? 'flex' : 'none',
    color: 'var(--wl-menu-text, #f1f5f9)',
    transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    '& .MuiSvgIcon-root': { fontSize: 20 },
  }),
  subMenuWrapper: {
    overflowY: 'auto',
    overflowX: 'hidden',
    transition:
      'max-height 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    maxHeight: '420px',
    opacity: 1,
    paddingBottom: '8px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  subMenuItem: (props) => ({
    padding: props.drawerWidth > collapsedDrawerWidth ? '8px 16px' : 0,
    minHeight: 'auto',
    borderRadius: '8px',
    marginLeft: props.drawerWidth > collapsedDrawerWidth ? '0' : 0,
    width: props.drawerWidth > collapsedDrawerWidth ? 'calc(100% - 24px)' : '100%',
    justifyContent: 'center',
    color: 'var(--wl-menu-text, #e2e8f0)',
    transition: 'background-color 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    '&:hover:not(.active)': {
      backgroundColor: 'var(--wl-menu-hover-bg, rgba(0, 0, 0, 0.12))',
      color: 'var(--wl-menu-hover-text, #ffffff)',
      '& .MuiListItemIcon-root': {
        color: 'var(--wl-menu-hover-text, #ffffff)',
      },
    },
    '&.active': {
      backgroundColor: 'var(--wl-menu-active-bg, rgba(0, 0, 0, 0.18))',
      color: 'var(--wl-menu-active-text, #ffffff)',
      borderLeft: '3px solid var(--wl-menu-active-indicator, #7DFFAF)',
      '& .MuiListItemIcon-root': {
        color: 'var(--wl-menu-active-text, #ffffff)',
      },
    },
  }),
  subMenuText: (props) => ({
    display: props.drawerWidth > collapsedDrawerWidth ? 'block' : 'none',
    fontSize: '13px',
    fontWeight: 500,
    color: 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  }),
  subMenuItemIcon: {
    minWidth: 28,
    color: 'var(--wl-menu-icon, #e2e8f0)',
    '& .MuiSvgIcon-root': { fontSize: 18 },
  },
  badge: {
    '& .MuiBadge-badge': {
      backgroundColor: '#ef4444',
      color: '#ffffff',
      fontSize: '10px',
      minWidth: '16px',
      height: '16px',
      top: '4px',
      right: '4px',
    },
  },
  menuDivider: (props) => ({
    width: props.drawerWidth > collapsedDrawerWidth ? '80%' : '32px',
    height: '1px',
    backgroundColor: 'var(--wl-menu-divider, rgba(255, 255, 255, 0.1))',
    margin: props.drawerWidth > collapsedDrawerWidth ? '12px auto' : '8px auto',
  }),
  // Content area: flex column, no scroll here (scroll is in contentScrollWrapper)
  content: () => ({
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    marginTop: '64px',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.2s ease',
    [theme.breakpoints.down('md')]: {
      marginTop: '64px',
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: 'calc(44px + env(safe-area-inset-top, 0px))',
      overflow: 'hidden',
      minHeight: 0,
    },
  }),
  // Offset extra quando o banner de trial está visível
  contentWithBanner: {
    marginTop: `${64 + TRIAL_BANNER_HEIGHT}px !important`,
    [theme.breakpoints.down('sm')]: {
      marginTop: `calc(44px + ${TRIAL_BANNER_HEIGHT}px + env(safe-area-inset-top, 0px)) !important`,
    },
  },
  // Sem scroll no layout; o scroll fica só no content de cada página
  contentScrollWrapper: () => ({
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      // Espaço para o mobile nav (64px + 12px padding) + safe-area do PWA (só quando o menu está visível)
      paddingBottom: 'calc(76px + env(safe-area-inset-bottom, 0px))',
    },
  }),
  // Dentro de conversa ou Flow Builder o menu inferior não aparece → sem padding para não subir o conteúdo
  contentScrollWrapperNoNavPadding: {
    [theme.breakpoints.down('sm')]: {
      paddingBottom: '0 !important',
    },
  },
  contentScrollInner: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '& > *': {
      flex: 1,
      minHeight: 0,
    },
  },
  // Mobile Bottom Navigation: fixed at viewport bottom; fill safe area so no gap below
  mobileBottomNav: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexShrink: 0,
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      minHeight: '76px',
      paddingTop: '6px',
      paddingLeft: '8px',
      paddingRight: '8px',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      backgroundColor: '#ffffff',
      borderTop: '1px solid #e5e7eb',
      justifyContent: 'space-around',
      alignItems: 'center',
      zIndex: 9,
      boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
    },
  },
  mobileNavItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px 8px',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: '#000000',
    minWidth: '52px',
    minHeight: '44px',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: 'rgba(59, 130, 246, 0.08)',
    },
    '&.active': {
      color: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.12)',
      '& $mobileNavIcon': {
        color: '#3b82f6',
      },
      '& $mobileNavLabel': {
        color: '#3b82f6',
        fontWeight: 600,
      },
    },
  },
  mobileNavIcon: {
    fontSize: '22px',
    marginBottom: '2px',
  },
  mobileNavLabel: {
    fontSize: '10px',
    fontWeight: 500,
    textAlign: 'center',
    lineHeight: 1.2,
  },
  mobileNavHomeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    flexShrink: 0,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
    marginTop: '-24px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
  },
  mobileNavHomeIcon: {
    fontSize: '28px',
  },
  contentWithMobileNav: {
    // padding da bottom nav já está em contentScrollWrapper; não duplicar
  },
  hideOnMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none !important',
    },
  },
  // Submenu popover (menu recolhido) – colado ao canto esquerdo (ao lado da barra)
  submenuPopoverPaper: {
    marginTop: '4px',
    marginLeft: 0,
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    minWidth: 180,
    left: `${collapsedDrawerWidth}px !important`,
    '& .MuiListItem-root': {
      fontSize: '14px',
    },
  },
  submenuPopoverItem: () => ({
    padding: '10px 16px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
    '& .MuiListItemIcon-root': {
      minWidth: 28,
      '& .MuiSvgIcon-root': { fontSize: 18 },
    },
    '&:hover': {
      backgroundColor: 'var(--wl-menu-hover-bg, rgba(0, 0, 0, 0.12))',
      color: 'var(--wl-menu-hover-text, #ffffff)',
    },
    '&.active': {
      backgroundColor: 'var(--wl-menu-active-bg, rgba(0, 0, 0, 0.18))',
      color: 'var(--wl-menu-active-text, #ffffff)',
      borderLeft: '3px solid var(--wl-menu-active-indicator, #7DFFAF)',
    },
  }),
  // Menu Dropdown
  dropdownMenu: {
    marginTop: '8px',
    '& .MuiPaper-root': {
      borderRadius: '12px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
      border: '1px solid #e5e7eb',
    },
  },
  dropdownItem: {
    padding: '0 16px',
    fontSize: '14px',
    height: '48px',
    minHeight: '48px',
    maxHeight: '48px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    '& .MuiListItemIcon-root': {
      minWidth: '36px',
    },
    '& .MuiListItemText-root': {
      margin: 0,
    },
    '&:hover': {
      backgroundColor: '#f3f4f6',
    },
  },
  dropdownItemProfile: {
    padding: '12px 12px',
    minHeight: '56px',
    '& .MuiListItemIcon-root': {
      marginRight: '-4px',
      minWidth: '44px',
    },
  },
  dropdownIcon: {
    minWidth: '36px',
    color: '#6b7280',
  },
  divider: {
    margin: '6px 0',
    backgroundColor: '#e5e7eb',
  },
  // Estilos do Banner IA SDR
  aiSdrBanner: {
    margin: '16px 12px 20px 12px',
    padding: '0',
    background: '#2a2a2a',
    borderRadius: '12px',
    color: 'white',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
    position: 'relative',
    overflow: 'hidden',
  },
  bannerContent: {
    padding: '16px',
    position: 'relative',
    zIndex: 2,
  },
  bannerTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'white !important',
    marginBottom: '4px',
    lineHeight: '1.3',
  },
  bannerSubtitle: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.7) !important',
    lineHeight: '1.4',
    fontWeight: '400',
    marginBottom: '12px',
  },
  bannerImage: {
    width: '100%',
    height: '120px',
    backgroundImage: `url(${f002Image})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: '8px',
    marginBottom: '12px',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(to top, rgba(0,0,0,0.3), transparent)',
      borderRadius: '8px',
    },
  },
  bannerButton: {
    width: '100%',
    padding: '10px 16px',
    background: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    color: '#2a2a2a',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    '&:hover': {
      background: '#f0f0f0',
      transform: 'translateY(-1px)',
    },
  },
}));

const LoggedInLayout = ({ children }) => {
  const { get: getSetting } = useSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const location = useLocation();
  const [drawerExpanded, setDrawerExpanded] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState(() => ({
    sistema: location.pathname.startsWith('/system') || location.pathname.startsWith('/sistema'),
    superAdmin: location.pathname.startsWith('/super-admin'),
    ajuda: location.pathname === '/helps' || location.pathname === '/tutorial-videos',
    vendas: location.pathname.startsWith('/sales') || location.pathname.startsWith('/vendas'),
    marketing:
      location.pathname.startsWith('/marketing') || location.pathname.startsWith('/campanhas'),
    contatos:
      location.pathname.startsWith('/contacts') || location.pathname.startsWith('/contatos'),
    atendimentos:
      location.pathname.startsWith('/tickets') || location.pathname.startsWith('/chats'),
    automacoes: location.pathname.startsWith('/automations'),
    faturas:
      location.pathname.startsWith('/subscription/invoice') ||
      location.pathname.startsWith('/faturas'),
    assinatura:
      location.pathname === '/subscription' || location.pathname.startsWith('/subscription/'),
  }));
  const drawerWidth = isMobile
    ? expandedDrawerWidth
    : drawerExpanded
      ? expandedDrawerWidth
      : collapsedDrawerWidth;
  const showMenuLabels = drawerWidth > collapsedDrawerWidth || isMobile;
  const primaryColor = theme.palette.primary.main || '#3b82f6';
  const classes = useStyles({
    drawerWidth,
    primaryColor,
    sistemaExpanded: expandedMenus.sistema,
    superAdminExpanded: expandedMenus.superAdmin,
  });

  const { user, handleLogout, loading, isMobileSession } = useContext(AuthContext);
  const {
    planActive,
    hasApp,
    loading: planLoading,
    trialActive,
    trialExpiringSoon,
    openAi: planOpenAi,
    integrations: planIntegrations,
    schedules: planSchedules,
    internalChat: planInternalChat,
  } = usePlanPermissions();
  const isSuperAdminUser = Boolean(user && user.super);
  const bannerVisible = !isSuperAdminUser && planActive && trialActive;
  const isUserProfile = user?.profile === 'user';

  // Rotas permitidas para perfil "user" (só essas aparecem no menu)
  const USER_ALLOWED_PATHS = useMemo(
    () =>
      new Set([
        '/kanban',
        '/tickets/conversation',
        '/tickets/quick-messages',
        '/sales/clients',
        '/sales/products',
        '/sales/services',
        '/contacts/contact-lists',
      ]),
    []
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [trialEndingModalOpen, setTrialEndingModalOpen] = useState(false);
  const [profileUrl, setProfileUrl] = useState(null);
  const [volume, setVolume] = useState(() => {
    const raw = localStorage.getItem('volume');
    const num = raw !== null && raw !== '' ? Number(raw) : NaN;
    return !Number.isFinite(num) ? 1 : Math.min(1, Math.max(0, num));
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [submenuPopoverAnchor, setSubmenuPopoverAnchor] = useState(null);
  const [submenuPopoverMenuKey, setSubmenuPopoverMenuKey] = useState(null);
  const [helpAnchor, setHelpAnchor] = useState(null);
  const [whatsappSupportPhone, setWhatsappSupportPhone] = useState('');

  const handleHelpOpen = (e) => setHelpAnchor(e.currentTarget);
  const handleHelpClose = () => setHelpAnchor(null);

  useEffect(() => {
    getSetting('whatsappSupportPhone')
      .then((data) => {
        if (data?.value) setWhatsappSupportPhone(data.value);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDrawerExpanded(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    setExpandedMenus((prev) => ({
      ...prev,
      sistema: location.pathname.startsWith('/system') || location.pathname.startsWith('/sistema'),
      superAdmin: location.pathname.startsWith('/super-admin'),
      ajuda: location.pathname === '/helps' || location.pathname === '/tutorial-videos',
      vendas: location.pathname.startsWith('/sales') || location.pathname.startsWith('/vendas'),
      marketing:
        location.pathname.startsWith('/marketing') || location.pathname.startsWith('/campanhas'),
      contatos:
        location.pathname.startsWith('/contacts') || location.pathname.startsWith('/contatos'),
      atendimentos:
        location.pathname.startsWith('/tickets') || location.pathname.startsWith('/chats'),
      automacoes: location.pathname.startsWith('/automations'),
      faturas:
        location.pathname.startsWith('/subscription/invoice') ||
        location.pathname.startsWith('/faturas'),
      assinatura:
        location.pathname === '/subscription' || location.pathname.startsWith('/subscription/'),
    }));
  }, [location.pathname]);

  useEffect(() => {
    if (user?.profileImage) {
      const nextUrl = `${backendUrl}/public/company${user.companyId}/user/${user.profileImage}`;
      setProfileUrl((current) => (current !== nextUrl ? nextUrl : current));
    } else {
      setProfileUrl((current) => (current !== null ? null : current));
    }
  }, [user?.profileImage, user?.companyId]);

  // BLOQUEIO PARA PLANOS VENCIDOS (ignorado para Super Admin)
  useEffect(() => {
    if (isSuperAdminUser) return;

    // Se ainda está carregando informações do plano, não faz nada
    if (planLoading || loading) return;

    // Se a conta está bloqueada (trial expirado, assinatura cancelada ou empresa inativa)
    // e o usuário não está já em uma página de resolução, redireciona e exibe o modal.
    const isOnBlockedPage =
      location.pathname === '/account-blocked' ||
      location.pathname === '/conta-bloqueada' ||
      location.pathname.startsWith('/subscription/');
    if (!planActive && !isOnBlockedPage) {
      history.push('/account-blocked');
      setBlockModalOpen(true);
      setTrialEndingModalOpen(false);
    }
  }, [planActive, planLoading, loading, location.pathname, history, isSuperAdminUser]);

  // Remove push subscription quando conta bloqueada (fatura vencida) — evita notificações indevidas
  useEffect(() => {
    if (!isSuperAdminUser && !planActive) {
      unsubscribeAndUnregister();
    }
  }, [planActive, isSuperAdminUser]);

  // Modal "período de teste acabando" — usa user.trial (SerializeUser do backend) como fonte de verdade
  useEffect(() => {
    if (isSuperAdminUser || !planActive) return;
    if (!user?.companyId) return;
    if (planLoading || loading) return;

    // Usa user.trial quando disponível (novo backend); fallback para dueDate durante migração
    const trial = user?.trial;
    const shouldShow = trial
      ? shouldShowTrialEndingModal(trial)
      : Boolean(user?.company?.dueDate && trialActive && trialExpiringSoon);

    if (!shouldShow) return;
    if (wasTrialEndingModalShownToday(user.companyId)) return;

    setTrialEndingModalOpen(true);
  }, [
    user?.companyId,
    user?.trial,
    user?.company?.dueDate,
    planActive,
    planLoading,
    loading,
    isSuperAdminUser,
    trialActive,
    trialExpiringSoon,
  ]);

  const handleTrialEndingModalClose = () => {
    markTrialEndingModalShownToday(user?.companyId);
    setTrialEndingModalOpen(false);
  };

  const handleUserMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    setUserMenuOpen(true);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
    setUserMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    setUserMenuOpen(false);
  };

  const handleOpenNotificationModal = () => {
    setNotificationModalOpen(true);
    setUserMenuOpen(false);
  };

  const handleLogoutClick = () => {
    handleLogout();
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSubmenuItemSelect = (path) => {
    history.push(path);
    setSubmenuPopoverAnchor(null);
    setSubmenuPopoverMenuKey(null);
    if (isMobile) setMobileOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Detectar se está dentro de uma conversa de ticket (para ocultar menu mobile e header)
  const isInsideTicketConversation = /\/tickets\/conversation\/[^/]+$/i.test(location.pathname);

  // Detectar se está no Flow Builder (para ocultar menu lateral)
  const isFlowBuilderPage =
    location.pathname === '/automations/flowbuilder' ||
    location.pathname.startsWith('/automations/flowbuilder/');

  /** Página de conversas internas: sem menu mobile inferior (layout tipo messenger). */
  const isInternalChatsPage = location.pathname.startsWith('/chats');
  const hideMobileBottomNav =
    isInsideTicketConversation || isFlowBuilderPage || isInternalChatsPage;

  const staticMenuItems = useMemo(() => {
    const showInternalChatAdminPage =
      (isSuperAdminUser || user?.profile === 'admin') &&
      (isSuperAdminUser || (!planLoading && planInternalChat));

    return [
      {
        title: 'Dashboard',
        path: '/dashboard',
        icon: <DashboardIcon />,
        exact: true,
        disabled: !planActive,
      },
      {
        title: 'CRM',
        path: '/kanban',
        icon: <ViewKanbanIcon />,
        disabled: !planActive,
      },
      {
        title: 'Atendimento',
        path: '/tickets',
        icon: <SupportAgentIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'Conversas',
            path: '/tickets/conversation',
            icon: <ChatIcon />,
          },
          {
            title: 'Mensagens rápidas',
            path: '/tickets/quick-messages',
            icon: <FlashOnIcon />,
          },
          {
            title: 'Departamentos',
            path: '/tickets/queues',
            icon: <QueueIcon />,
          },
          { title: 'Tags', path: '/tickets/tags', icon: <LocalOfferIcon /> },
          {
            title: 'Canais',
            path: '/tickets/channels',
            icon: <DeviceHubIcon />,
          },
          ...(isSuperAdminUser || (!planLoading && planSchedules)
            ? [
                {
                  title: 'Mensagens agendadas',
                  path: '/tickets/schedules',
                  icon: <ScheduleIcon />,
                },
              ]
            : []),
          ...(showInternalChatAdminPage
            ? [
                {
                  title: 'Chats internos',
                  path: '/chats',
                  icon: <ForumIcon />,
                },
              ]
            : []),
        ],
      },
      {
        title: 'Vendas',
        path: '/sales',
        icon: <BusinessCenterIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'Clientes',
            path: '/sales/clients',
            icon: <PersonIcon />,
          },
          ...(isSuperAdminUser || hasApp('products')
            ? [{ title: 'Produtos', path: '/sales/products', icon: <ShoppingCartIcon /> }]
            : []),
          ...(isSuperAdminUser || hasApp('services')
            ? [{ title: 'Serviços', path: '/sales/services', icon: <BuildIcon /> }]
            : []),
        ],
      },
      {
        title: 'Marketing',
        path: '/marketing',
        icon: <CampaignIcon />,
        disabled: !planActive,
        subItems: [
          { title: 'FlowUp', path: '/marketing/flowup', icon: <TrendingUpIcon /> },
          {
            title: 'Campanhas por frase',
            path: '/marketing/campaign-by-phrase',
            icon: <MessageIcon />,
          },
          ...(isSuperAdminUser || hasApp('mass_dispatch')
            ? [{ title: 'Disparo em massa', path: '/marketing/mass-shooting', icon: <SendIcon /> }]
            : []),
        ],
      },
      {
        title: 'Contatos',
        path: '/contacts',
        icon: <ContactsIcon />,
        disabled: !planActive,
        subItems: [
          {
            title: 'Leads',
            path: '/contacts/leads',
            icon: <PersonAddIcon />,
            disabled: !planActive,
          },
          {
            title: 'Listas segmentadas (tags)',
            path: '/contacts/contact-lists',
            icon: <ListIcon />,
          },
          {
            title: 'Importar contatos',
            path: '/contacts/import-contacts',
            icon: <CloudUploadIcon />,
          },
          // {
          //   title: 'Configurações de Contatos',
          //   path: '/contacts/settings',
          //   icon: <SettingsIcon />,
          // },
        ],
      },
      {
        title: 'Automações',
        path: '/automations',
        icon: <ExtensionIcon />,
        disabled: !planActive,
        subItems: [
          ...(isSuperAdminUser || (!planLoading && planOpenAi)
            ? [
                {
                  title: 'IA Workflows',
                  path: '/automations/ia-workflows',
                  icon: <AccountTreeIcon />,
                },
                {
                  title: 'Agentes de IA',
                  path: '/automations/agents',
                  icon: <PersonIcon />,
                },
              ]
            : []),
          {
            title: 'Construtor de fluxo',
            path: '/automations/flowbuilders',
            icon: <AccountTreeIcon />,
          },
          ...(isSuperAdminUser || (!planLoading && planIntegrations)
            ? [
                {
                  title: 'Integrações',
                  path: '/automations/integration',
                  icon: <DeviceHubIcon />,
                },
              ]
            : []),
          ...(isSuperAdminUser
            ? [
                {
                  title: 'Mensagens automáticas',
                  path: '/automations/message',
                  icon: <MessageIcon />,
                },
              ]
            : []),
        ],
      },
      {
        title: 'Apps',
        path: '/apps',
        icon: <AppsIcon />,
        disabled: !planActive,
      },
      {
        title: 'Assinatura',
        path: '/subscription/plans',
        icon: <CreditCardIcon />,
        subItems: [
          {
            title: 'Planos',
            path: '/subscription/plans',
            icon: <LoyaltyIcon />,
          },
          {
            title: 'Faturas',
            path: '/subscription/invoice',
            icon: <ReceiptIcon />,
          },
        ],
      },
      {
        title: 'Configurações',
        path: '/system',
        icon: <BuildIcon />,
        disabled: !planActive,
        subItems: [
          { title: 'Usuários', path: '/system/users', icon: <PersonIcon /> },
          // { title: 'Produtivo', path: '/system/produtivo', icon: <BarChartIcon /> },
        ],
      },
      ...(isSuperAdminUser
        ? [
            {
              title: 'White Label',
              path: '/super-admin',
              icon: <AdminPanelSettingsIcon />,
              disabled: !planActive,
              subItems: [
                { title: 'Empresas', path: '/super-admin/companies', icon: <BusinessIcon /> },
                { title: 'Faturas', path: '/super-admin/invoices', icon: <ReceiptIcon /> },
                {
                  title: 'Financeiro (Relatórios)',
                  path: '/super-admin/financial',
                  icon: <ReceiptIcon />,
                },
                { title: 'Planos', path: '/super-admin/plans', icon: <LoyaltyIcon /> },
                { title: 'Apps', path: '/super-admin/apps', icon: <AppsIcon /> },
                { title: 'Personalização', path: '/super-admin/whitelabel', icon: <PaletteIcon /> },
                {
                  title: 'Credenciais',
                  path: '/super-admin/payment-credentials',
                  icon: <VpnKeyIcon />,
                },
                { title: 'Configurações', path: '/super-admin/settings', icon: <SettingsIcon /> },
              ],
            },
          ]
        : []),
      {
        title: 'Central de Ajuda',
        path: '/helps',
        icon: <HelpOutlineIcon />,
        disabled: !planActive,
        subItems: [
          { title: 'Ajuda', path: '/helps', icon: <HelpOutlineIcon /> },
          ...(isSuperAdminUser
            ? [{ title: 'Vídeos Tutoriais', path: '/tutorial-videos', icon: <VideoLibraryIcon /> }]
            : []),
        ],
      },
    ];
  }, [
    planActive,
    hasApp,
    location.pathname,
    isSuperAdminUser,
    user?.profile,
    planLoading,
    planOpenAi,
    planIntegrations,
    planSchedules,
    planInternalChat,
  ]);

  // Para perfil "user", exibir apenas os itens permitidos
  const menuItems = useMemo(() => {
    if (!isUserProfile) return staticMenuItems;
    return staticMenuItems
      .map((item) => {
        if (item.subItems) {
          const allowedSubItems = item.subItems.filter((sub) => USER_ALLOWED_PATHS.has(sub.path));
          if (allowedSubItems.length === 0) return null;
          return { ...item, subItems: allowedSubItems };
        }
        return USER_ALLOWED_PATHS.has(item.path) ? item : null;
      })
      .filter(Boolean);
  }, [staticMenuItems, isUserProfile, USER_ALLOWED_PATHS]);

  const MenuItemWithTooltip = ({ path, icon, title, exact = false, disabled = false }) => {
    const isActive = exact
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + '/');

    const handleClick = (event) => {
      if (disabled) {
        event.preventDefault();
        // Mostra alerta ao tentar acessar com plano vencido
        alert(
          '⚠️ ACESSO BLOQUEADO!\n\nSeu plano está vencido. Para acessar esta funcionalidade, por favor, regularize seu pagamento na página Financeiro.'
        );
        return;
      }
      if (!isActive) {
        history.push(path);
      }
    };

    const item = (
      <ListItem
        button
        disabled={disabled}
        onClick={handleClick}
        className={`${classes.menuItem} ${isActive ? 'active' : ''}`}
      >
        <ListItemIcon className={classes.menuIcon}>{icon}</ListItemIcon>
        {showMenuLabels && <ListItemText primary={title} classes={{ primary: classes.menuText }} />}
      </ListItem>
    );

    if (showMenuLabels) {
      return item;
    }

    return (
      <Tooltip title={title} placement="right" arrow>
        {item}
      </Tooltip>
    );
  };

  const DrawerContent = () => (
    <div>
      <div className={classes.drawerHeader}>
        {drawerExpanded ? (
          <>
            <div className={classes.drawerHeaderLogo}>
              <div className={classes.logo} onClick={() => history.push('/dashboard')}>
                {theme.appLogoLight || theme.calculatedLogoLight?.() ? (
                  <img
                    src={
                      theme.calculatedLogoLight ? theme.calculatedLogoLight() : theme.appLogoLight
                    }
                    alt="Logo"
                    className={classes.companyLogo}
                  />
                ) : (
                  <DashboardIcon className={classes.logoIcon} />
                )}
              </div>
            </div>
            <Tooltip title="Recolher menu" placement="right">
              <IconButton
                onClick={() => !isMobile && setDrawerExpanded(false)}
                className={classes.menuToggleButton}
                size="small"
                disableRipple
              >
                <FormatIndentDecreaseIcon className={classes.menuToggleIconCombo} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Abrir menu" placement="right">
            <IconButton
              onClick={() => !isMobile && setDrawerExpanded(true)}
              className={classes.menuToggleButton}
              size="small"
              disableRipple
            >
              <FormatIndentIncreaseIcon className={classes.menuToggleIconCombo} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div className={classes.sidebarContent}>
        <List className={classes.menuList}>
          <div className={classes.menuSectionLabel}>Principais</div>
          {menuItems.map((item) => {
            if (item.subItems) {
              const menuKey =
                item.path === '/system'
                  ? 'sistema'
                  : item.path === '/super-admin'
                    ? 'superAdmin'
                    : item.path === '/helps'
                      ? 'ajuda'
                      : item.path === '/sales'
                        ? 'vendas'
                        : item.path === '/marketing'
                          ? 'marketing'
                          : item.path === '/contacts'
                            ? 'contatos'
                            : item.path === '/tickets'
                              ? 'atendimentos'
                              : item.path === '/automations'
                                ? 'automacoes'
                                : item.path === '/subscription/invoice'
                                  ? 'faturas'
                                  : item.path === '/subscription/plans'
                                    ? 'assinatura'
                                    : null;
              const isMenuActive =
                item.path === '/helps'
                  ? location.pathname === '/helps' || location.pathname === '/tutorial-videos'
                  : item.path === '/marketing'
                    ? location.pathname.startsWith('/marketing') ||
                      location.pathname.startsWith('/campanhas')
                    : item.path === '/subscription/plans'
                      ? location.pathname === '/subscription' ||
                        location.pathname.startsWith('/subscription/')
                      : item.path === '/tickets'
                        ? location.pathname.startsWith('/tickets') ||
                          location.pathname.startsWith('/chats')
                        : location.pathname === item.path ||
                          location.pathname.startsWith(item.path + '/');
              const isExpanded = menuKey ? expandedMenus[menuKey] : false;
              const handleParentClick = (event) => {
                if (item.disabled) {
                  alert(
                    '⚠️ ACESSO BLOQUEADO!\n\nSeu plano está vencido. Para acessar esta funcionalidade, por favor, regularize seu pagamento na página Financeiro.'
                  );
                  return;
                }
                if (showMenuLabels) {
                  if (menuKey) {
                    setExpandedMenus((prev) => ({
                      ...prev,
                      [menuKey]: !prev[menuKey],
                    }));
                  }
                } else {
                  setSubmenuPopoverAnchor(event.currentTarget);
                  setSubmenuPopoverMenuKey(menuKey);
                }
              };
              const parentItem = (
                <ListItem
                  button
                  disabled={item.disabled}
                  onClick={handleParentClick}
                  className={`${classes.menuItem} ${isMenuActive ? 'active' : ''}`}
                >
                  <ListItemIcon className={classes.menuIcon}>{item.icon}</ListItemIcon>
                  {showMenuLabels && (
                    <>
                      <ListItemText primary={item.title} classes={{ primary: classes.menuText }} />
                      {isExpanded ? (
                        <ExpandLessIcon className={classes.menuItemExpandIcon} />
                      ) : (
                        <ExpandMoreIcon className={classes.menuItemExpandIcon} />
                      )}
                    </>
                  )}
                </ListItem>
              );
              return (
                <React.Fragment key={item.path}>
                  {showMenuLabels ? (
                    parentItem
                  ) : (
                    <Tooltip title={item.title} placement="right" arrow>
                      {parentItem}
                    </Tooltip>
                  )}
                  {showMenuLabels && isExpanded && (
                    <div className={classes.subMenuWrapper}>
                      {item.subItems.map((sub) => {
                        const isSubActive =
                          location.pathname === sub.path ||
                          location.pathname.startsWith(sub.path + '/');
                        const handleSubClick = () => {
                          if (!item.disabled) history.push(sub.path);
                        };
                        return (
                          <ListItem
                            key={sub.path}
                            button
                            disabled={item.disabled}
                            onClick={handleSubClick}
                            className={`${classes.subMenuItem} ${isSubActive ? 'active' : ''}`}
                          >
                            {sub.icon && (
                              <ListItemIcon className={classes.subMenuItemIcon}>
                                {sub.icon}
                              </ListItemIcon>
                            )}
                            <ListItemText
                              primary={sub.title}
                              classes={{ primary: classes.subMenuText }}
                            />
                          </ListItem>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            }
            return <MenuItemWithTooltip key={item.path} {...item} />;
          })}
        </List>

        <Menu
          anchorEl={submenuPopoverAnchor}
          open={Boolean(submenuPopoverAnchor)}
          onClose={() => {
            setSubmenuPopoverAnchor(null);
            setSubmenuPopoverMenuKey(null);
          }}
          anchorReference="anchorPosition"
          anchorPosition={
            submenuPopoverAnchor
              ? {
                  left: collapsedDrawerWidth,
                  top: submenuPopoverAnchor.getBoundingClientRect().top,
                }
              : undefined
          }
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            className: classes.submenuPopoverPaper,
            elevation: 8,
          }}
          transitionDuration={200}
        >
          {menuItems
            .find(
              (i) =>
                i.subItems &&
                ((i.path === '/system' && submenuPopoverMenuKey === 'sistema') ||
                  (i.path === '/super-admin' && submenuPopoverMenuKey === 'superAdmin') ||
                  (i.path === '/helps' && submenuPopoverMenuKey === 'ajuda') ||
                  (i.path === '/sales' && submenuPopoverMenuKey === 'vendas') ||
                  (i.path === '/marketing' && submenuPopoverMenuKey === 'marketing') ||
                  (i.path === '/contacts' && submenuPopoverMenuKey === 'contatos') ||
                  (i.path === '/tickets' && submenuPopoverMenuKey === 'atendimentos') ||
                  (i.path === '/automations' && submenuPopoverMenuKey === 'automacoes') ||
                  (i.path === '/subscription/plans' && submenuPopoverMenuKey === 'assinatura'))
            )
            ?.subItems.map((sub) => {
              const isSubActive =
                location.pathname === sub.path || location.pathname.startsWith(sub.path + '/');
              return (
                <MenuItem
                  key={sub.path}
                  onClick={() => handleSubmenuItemSelect(sub.path)}
                  className={`${classes.submenuPopoverItem} ${isSubActive ? 'active' : ''}`}
                >
                  {sub.icon && (
                    <ListItemIcon className={classes.subMenuItemIcon}>{sub.icon}</ListItemIcon>
                  )}
                  {sub.title}
                </MenuItem>
              );
            })}
        </Menu>
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      {/* Sidebar Desktop */}
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Sidebar Mobile */}
      <Drawer
        className={classes.mobileDrawer}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <DrawerContent />
      </Drawer>

      {/* Banner de trial — fixo no topo do viewport, acima do header */}
      {bannerVisible && (
        <div className={classes.trialBannerWrapper}>
          <TrialBanner />
        </div>
      )}

      {/* Header - Sempre visível */}
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        className={classes.appBar}
        style={{ top: bannerVisible ? TRIAL_BANNER_HEIGHT : 0 }}
      >
        <Toolbar className={classes.toolbar}>
          {/* Seção Esquerda */}
          <div className={classes.headerLeft}>
            {/* Back Button - Visível apenas no Flow Builder */}
            {isFlowBuilderPage && (
              <IconButton
                className={classes.menuButton}
                onClick={() => history.push('/automations/flowbuilders')}
                edge="start"
                title="Voltar para lista de fluxos"
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            {/* Menu Button Mobile - Oculto só no Flow Builder; na conversa o voltar fica no ticker */}
            {!isFlowBuilderPage && (
              <IconButton className={classes.menuButton} onClick={handleDrawerToggle} edge="start">
                <MenuIcon />
              </IconButton>
            )}

            {/* Busca - Oculto no mobile */}
            <div
              className={classes.searchContainer}
              onClick={() => setSearchModalOpen(true)}
              style={{ cursor: 'pointer' }}
            >
              <div className={classes.searchIcon}>
                <SearchIcon />
              </div>
              <InputBase
                placeholder="Buscar conversas..."
                classes={{
                  root: classes.inputRoot,
                  input: classes.inputInput,
                }}
                readOnly
                style={{ cursor: 'pointer' }}
              />
              <Button className={classes.searchButton}>
                <SearchIcon style={{ fontSize: 18 }} />
              </Button>
            </div>

            {/* Logo - Visível apenas no mobile */}
            <div className={classes.mobileLogo}>
              {theme.appLogoLight || theme.calculatedLogoLight?.() ? (
                <img
                  src={theme.calculatedLogoLight ? theme.calculatedLogoLight() : theme.appLogoLight}
                  alt="Logo"
                />
              ) : (
                <img src={logo} alt="Logo" />
              )}
            </div>
          </div>

          {/* Seção Direita */}
          <div className={classes.headerRight}>
            {/* Botão Atendimentos */}
            <IconButton
              className={classes.iconButton}
              onClick={() => history.push('/tickets/conversation')}
              title="Atendimentos"
            >
              <QuestionAnswerIcon style={{ fontSize: 24 }} />
            </IconButton>

            {/* Botão Ajuda */}
            <IconButton className={classes.iconButton} onClick={handleHelpOpen} title="Ajuda">
              <HelpOutlineIcon style={{ fontSize: 28 }} />
            </IconButton>

            {/* Menu Central de Suporte */}
            <Menu
              anchorEl={helpAnchor}
              open={Boolean(helpAnchor)}
              onClose={handleHelpClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              getContentAnchorEl={null}
              PaperProps={{
                style: {
                  borderRadius: 16,
                  width: 268,
                  padding: '14px 12px 12px',
                  boxShadow: '0 8px 28px rgba(0,0,0,0.13)',
                },
              }}
            >
              {/* Cabeçalho */}
              <Box style={{ paddingLeft: 2, marginBottom: 12 }}>
                <Typography
                  style={{ fontWeight: 700, fontSize: 14.5, color: '#111827', lineHeight: 1.3 }}
                >
                  Central de Suporte
                </Typography>
                <Typography
                  style={{ fontSize: 12, color: '#9ca3af', marginTop: 2, lineHeight: 1.4 }}
                >
                  Como podemos ajudar hoje?
                </Typography>
              </Box>

              {/* Item WhatsApp */}
              <Box
                onClick={() => {
                  if (whatsappSupportPhone)
                    window.open(`https://wa.me/${whatsappSupportPhone}`, '_blank');
                  handleHelpClose();
                }}
                style={{
                  backgroundColor: '#f0fdf4',
                  borderRadius: 10,
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  marginBottom: 8,
                }}
              >
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FaWhatsapp size={17} color="#fff" />
                </Box>
                <Box>
                  <Typography
                    style={{ fontWeight: 600, fontSize: 13, color: '#15803d', lineHeight: 1.3 }}
                  >
                    WhatsApp
                  </Typography>
                  <Typography style={{ fontSize: 11.5, color: '#86efac', lineHeight: 1.4 }}>
                    Atendimento em tempo real
                  </Typography>
                </Box>
              </Box>

              {/* Item Treinamento */}
              <Box
                onClick={() => {
                  history.push('/helps');
                  handleHelpClose();
                }}
                style={{
                  backgroundColor: '#eff6ff',
                  borderRadius: 10,
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                }}
              >
                <Box
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <OndemandVideoIcon style={{ fontSize: 18, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography
                    style={{ fontWeight: 600, fontSize: 13, color: '#1d4ed8', lineHeight: 1.3 }}
                  >
                    Treinamento
                  </Typography>
                  <Typography style={{ fontSize: 11.5, color: '#93c5fd', lineHeight: 1.4 }}>
                    Vídeo aulas da ferramenta
                  </Typography>
                </Box>
              </Box>
            </Menu>

            {/* Botão Refresh */}
            <IconButton
              className={classes.iconButton}
              onClick={handleRefreshPage}
              title="Atualizar"
            >
              <CachedIcon style={{ fontSize: 24 }} />
            </IconButton>

            {/* Sino / Notificações */}
            <NotificationSettingsPopover
              volume={volume}
              setVolume={setVolume}
              onOpenNotifications={handleOpenNotificationModal}
              primaryColor={primaryColor}
            />

            {/* Avatar do Usuário */}
            <Avatar className={classes.avatar} src={profileUrl} onClick={handleUserMenuClick}>
              {!profileUrl && <PersonIcon style={{ fontSize: 30 }} />}
            </Avatar>
          </div>
        </Toolbar>
      </AppBar>

      {/* Menu do Usuário */}
      <Menu
        anchorEl={anchorEl}
        open={userMenuOpen}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className={classes.dropdownMenu}
      >
        <MenuItem
          onClick={handleOpenUserModal}
          className={`${classes.dropdownItem} ${classes.dropdownItemProfile}`}
        >
          <ListItemIcon className={classes.dropdownIcon}>
            <Avatar className={classes.dropdownAvatar} src={profileUrl}>
              {!profileUrl && (user?.name ? user.name.charAt(0).toUpperCase() : <PersonIcon />)}
            </Avatar>
          </ListItemIcon>
          <ListItemText primary={user?.name || 'Meu Perfil'} />
        </MenuItem>

        <Divider className={classes.divider} />

        <MenuItem onClick={handleLogoutClick} className={classes.dropdownItem}>
          <ListItemIcon className={classes.dropdownIcon}>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText primary={isMobileSession ? 'Sair do app' : 'Sair'} />
        </MenuItem>
      </Menu>

      {/* Modal do Usuário */}
      {userModalOpen && (
        <UserModal
          open={userModalOpen}
          onClose={() => setUserModalOpen(false)}
          onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
          userId={user?.id}
        />
      )}

      {/* Modal de Notificações */}
      <NotificationModal
        open={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
      />

      {/* Modal de bloqueio (fatura vencida) */}
      <BlockedModal open={blockModalOpen} onClose={() => setBlockModalOpen(false)} />

      {/* Modal período de teste acabando — plano ativo; nunca quando bloqueado */}
      <TrialEndingModal
        open={trialEndingModalOpen && planActive && !blockModalOpen}
        onClose={handleTrialEndingModalClose}
        trial={user?.trial}
        dueDate={user?.company?.dueDate}
      />

      {/* PWA: dynamic head, install prompt, e aviso para desinstalar/reinstalar quando a versão do ícone sobe */}
      <PwaHeadUpdater />
      <PWAInstallPrompt />
      <PwaReinstallModal />

      {/* Modal de Busca de Conversas */}
      <SearchTicketModal open={searchModalOpen} onClose={() => setSearchModalOpen(false)} />

      {/* Widget global: só com chat interno no plano (super ignora plano). Escondido na página /chats para admin/super. */}
      <InternalChatWidget
        disabled={
          (!isSuperAdminUser && (planLoading || !planInternalChat)) ||
          ((user?.super || user?.profile === 'admin') && location.pathname.startsWith('/chats'))
        }
      />

      {/* Conteúdo Principal: scroll só dentro do wrapper; global header e (em páginas) page header ficam fixos */}
      <main
        id="app-main-content"
        className={`${classes.content} ${bannerVisible ? classes.contentWithBanner : ''} ${!hideMobileBottomNav ? classes.contentWithMobileNav : ''}`}
      >
        <div
          className={`${classes.contentScrollWrapper} ${hideMobileBottomNav ? classes.contentScrollWrapperNoNavPadding : ''}`}
        >
          <div className={classes.contentScrollInner}>
            <Suspense
              fallback={
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="280px"
                  width="100%"
                >
                  <CircularProgress size={36} />
                </Box>
              }
            >
              {children}
            </Suspense>
          </div>
        </div>
      </main>

      {/* Menu Mobile Fixo - Oculto no desktop, em conversa de ticket, Flow Builder e /chats */}
      {!hideMobileBottomNav && (
        <div className={classes.mobileBottomNav}>
          {/* Botão 1 - Dashboard */}
          <div
            className={`${classes.mobileNavItem} ${isActivePath('/dashboard') ? 'active' : ''}`}
            onClick={() => history.push('/dashboard')}
          >
            <DashboardIcon className={classes.mobileNavIcon} />
            <span className={classes.mobileNavLabel}>Dashboard</span>
          </div>

          {/* Botão 2 - Contatos (página Importar contatos) */}
          <div
            className={`${classes.mobileNavItem} ${isActivePath('/contacts/import-contacts') ? 'active' : ''}`}
            onClick={() => history.push('/contacts/import-contacts')}
          >
            <ContactsIcon className={classes.mobileNavIcon} />
            <span className={classes.mobileNavLabel}>Contatos</span>
          </div>

          {/* Botão Home (Centro) - Tickets */}
          <div
            className={classes.mobileNavHomeBtn}
            onClick={() => history.push('/tickets/conversation')}
          >
            <ChatIcon className={classes.mobileNavHomeIcon} />
          </div>

          {/* Botão 4 - Canais (mesmo path do menu: Atendimento > Canais) */}
          <div
            className={`${classes.mobileNavItem} ${isActivePath('/tickets/channels') ? 'active' : ''}`}
            onClick={() => history.push('/tickets/channels')}
          >
            <DeviceHubIcon className={classes.mobileNavIcon} />
            <span className={classes.mobileNavLabel}>Canais</span>
          </div>

          {/* Botão 5 - Faturas */}
          <div
            className={`${classes.mobileNavItem} ${isActivePath('/subscription/invoice') ? 'active' : ''}`}
            onClick={() => history.push('/subscription/invoice')}
          >
            <ReceiptIcon className={classes.mobileNavIcon} />
            <span className={classes.mobileNavLabel}>Faturas</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoggedInLayout;
